import * as React from 'react';

import { useTranslation } from 'react-i18next';
import {
  changeTimeZoneKeepWallClock,
  getWallClock,
  setWallClock,
  ZonedDateTime,
} from 'utils/dateTime';
import { getTimeZoneOptionsIncluding } from 'utils/timeZones';

import styles from './DatePicker.module.css';
import RenderDatePicker from '../../components/ProductClassAttributesInput/RenderDatePicker';

interface DatePickerProps {
  label: string;
  value: ZonedDateTime;
  setValue: (d: ZonedDateTime) => void;
  onTimezoneChange?: (tz: string) => void;
}

const range = (start, stop, step) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

/**
 * Generic datetime picker
 *
 * Edits the wall-clock time of a ZonedDateTime in its own time zone.
 */
export function DatePicker(props: DatePickerProps) {
  const { t } = useTranslation();
  const { value, setValue } = props;
  const wallClock = getWallClock(value);
  const hour = wallClock.getHours();
  const timeZoneList = React.useMemo(
    () => getTimeZoneOptionsIncluding(value.timeZone),
    [value.timeZone],
  );

  return (
    <div className={styles.container}>
      <label>{props.label}</label>
      <RenderDatePicker
        name="date-selection"
        input={{
          value: wallClock,
          onChange: (picked: Date | null) => {
            if (!picked || isNaN(picked.getTime())) return;
            setValue(
              setWallClock(value, {
                year: picked.getFullYear(),
                month: picked.getMonth(),
                date: picked.getDate(),
              }),
            );
          },
        }}
        fullWidth
      />
      <div className={styles.timePicker}>
        <div className={styles.timeContainer}>
          <label>{t('datepicker.hour')}</label>
          <select
            name="hour-selection"
            onChange={(e) => {
              setValue(
                setWallClock(value, {
                  hours:
                    (parseInt(e.target.value) + (hour >= 12 ? 12 : 0)) % 24,
                }),
              );
            }}
            value={((hour + 11) % 12) + 1}
          >
            {range(0, 11, 1).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.timeContainer}>
          <label>{t('datepicker.minute')}</label>
          <select
            name="minute-selection"
            onChange={(e) => {
              setValue(
                setWallClock(value, { minutes: parseInt(e.target.value) }),
              );
            }}
            value={wallClock.getMinutes()}
          >
            <option>0</option>
            <option>15</option>
            <option>30</option>
            <option>45</option>
          </select>
        </div>
        <div className={styles.timeContainer}>
          <label>{t('datepicker.daySelection')}</label>
          <select
            name="am-pm-selection"
            onChange={() => {
              setValue(setWallClock(value, { hours: (hour + 12) % 24 }));
            }}
            value={hour < 12 ? 'AM' : 'PM'}
          >
            <option>AM</option>
            <option>PM</option>
          </select>
        </div>
        <div className={styles.timeContainer}>
          <label>{t('datepicker.timezone')}</label>
          <select
            name={'time-zone-selection'}
            onChange={(e) => {
              setValue(changeTimeZoneKeepWallClock(value, e.target.value));
              props.onTimezoneChange && props.onTimezoneChange(e.target.value);
            }}
            value={value.timeZone}
          >
            {timeZoneList.map((tz) => {
              return (
                <option key={tz.name} value={tz.name}>
                  {tz.reformatName}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    </div>
  );
}
